import { useEffect, useRef, useState } from 'react'
import {
  ApiError, addPenCount, fishState, penCounts,
  type PenCount, type PenRollup,
} from '../api'
import { useT } from '../lang'

/* Registering the week's count: every ring, one screen, one pass.
 *
 * A GRID AND NOT A WIZARD, and that is the whole design.
 *
 * `Telling.tsx` is a good wizard and it is the right instrument for one count
 * taken now — it asks one thing at a time because the person is at the cage
 * with the fish in their hand. This is the other half of the job and it is a
 * different act: somebody sitting down, dry, with a filled sheet of paper or a
 * phone note in front of them, transcribing eight rings. There a wizard is
 * strictly slower than a grid, and being precious about the wizard would make
 * us slower than Excel at the one job the customer measures us on.
 *
 * WHAT IT HAS TO BEAT. Their spreadsheet is one row per session with the
 * columns exploding sideways — `merd 1 | antall talt | voksne | bevegelige |
 * fastsittende | merd 2 | …` — and it is genuinely fast to type into. So this
 * has to be faster, not merely nicer:
 *
 *   - Every ring is already on screen, in walking order, with its number
 *     printed. No row to create, nothing to name.
 *   - The fish count is entered ONCE at the top and fills every row, because
 *     vedlegg 1 asks for the same ten fish from every ring and typing 10 eight
 *     times is the kind of work that makes somebody stop halfway.
 *   - Enter goes DOWN a column, not across a row. A sheet is read down one
 *     stage at a time — all the adult females first — and tab-across forces the
 *     reader to zigzag.
 *   - The sea temperature is one field for the farm, not one per ring.
 *
 * A RING THAT COULD NOT BE COUNTED IS A ROW, NOT A BLANK. Weather, a delousing,
 * handling, an empty ring. Mattilsynet asks why a ring is missing, silence is
 * not an answer, and an absent row cannot become one afterwards.
 */

const REASONS = ['weather', 'treatment', 'handling', 'no_fish', 'equipment',
                 'other'] as const

/** Vedlegg 1: ten fish from every ring, twenty inside the spring window. */
const FISK_PER_MERD = 10

interface Row {
  adult: string
  mobile: string
  sessile: string
  n: string
  reason: string
}

const BLANK: Row = { adult: '', mobile: '', sessile: '', n: '', reason: '' }

const tall = (v: number | null | undefined) =>
  (v === null || v === undefined ? '' : String(v).replace('.', ','))

/** Norwegian decimal comma, because that is what the keyboard and the sheet
 *  both produce. Empty is null and not zero: "we did not enter that stage" and
 *  "we counted and there were none" are different claims. */
function num(s: string): number | null {
  const v = s.trim().replace(',', '.')
  if (v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const today = () => new Date().toISOString().slice(0, 10)

export function Merdtelling({
  siteId, initialDate, focusPen, onSaved, onClose,
}: {
  siteId: string
  initialDate?: string
  /** Opened from a ring on the map: that row gets the cursor. */
  focusPen?: string | null
  onSaved: (items: PenCount[], site: PenRollup | null) => void
  onClose: () => void
}) {
  const t = useT()
  const [date, setDate] = useState(initialDate ?? today())
  const [items, setItems] = useState<PenCount[] | null>(null)
  const [rows, setRows] = useState<Record<string, Row>>({})
  const [perPen, setPerPen] = useState(String(FISK_PER_MERD))
  const [temp, setTemp] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(0)
  /* Which rings held fish ON THE DAY BEING COUNTED. `null` while we do not
     know yet or where the farm has not modelled its fish at all — and those
     two must both fall back to showing every ring, because a farm that has
     not told us what stands where still has to be able to count. */
  const [medFisk, setMedFisk] = useState<Set<string> | null>(null)
  const box = useRef<HTMLDivElement>(null)

  /* What is already recorded for this day, so that reopening the form is a
     correction rather than a second count. Reloaded whenever the date moves:
     the same eight rings mean something different on a different day. */
  useEffect(() => {
    let dead = false
    setErr(null)
    void penCounts(siteId, date).then((r) => {
      if (dead) return
      setItems(r.items)
      const next: Record<string, Row> = {}
      for (const p of r.items) {
        next[p.pen_id] = p.pen_count_id
          ? { adult: tall(p.adult_female), mobile: tall(p.mobile),
              sessile: tall(p.sessile), n: tall(p.n_fish),
              reason: p.not_counted_reason ?? '' }
          : { ...BLANK }
      }
      setRows(next)
    }).catch((e) => {
      if (!dead) setErr(e instanceof ApiError ? (e.detail ?? e.code)
        : t('Could not reach the receiver.'))
    })
    return () => { dead = true }
  }, [siteId, date])

  /* AN EMPTY RING IS NOT A COUNTING ROW, AND WHICH RINGS ARE EMPTY DEPENDS ON
     THE DAY.
     A blank line for a ring with nothing in it invites somebody to write a
     zero there, and a zero is a claim that fish were looked at. It is also not
     a fixed list: merd 6 held H25 until Thursday morning and H25-AC after it,
     and an array that is right for today is wrong for last month. So this is
     keyed on `date` exactly as the counts are, and moving the date re-asks the
     question rather than filtering yesterday's answer. */
  useEffect(() => {
    let dead = false
    setMedFisk(null)
    void fishState(siteId, date).then((r) => {
      if (dead) return
      const occ = r.occupancy ?? []
      // No occupancy at all is "this farm has not said what stands where",
      // which is every farm until it does. Not "every ring is empty".
      setMedFisk(occ.length ? new Set(occ.map((o) => o.pen_id)) : null)
    }).catch(() => { if (!dead) setMedFisk(null) })
    return () => { dead = true }
  }, [siteId, date])

  useEffect(() => {
    if (!items || !focusPen) return
    const el = box.current?.querySelector<HTMLInputElement>(
      `input[data-cell="${focusPen}-0"]`)
    el?.focus()
  }, [items, focusPen])

  const set = (penId: string, p: Partial<Row>) =>
    setRows((r) => ({ ...r, [penId]: { ...(r[penId] ?? BLANK), ...p } }))

  /* Enter moves DOWN. A counting sheet is read one stage at a time — every
     ring's adult females, then every ring's mobiles — so the natural next cell
     is the one below, and tab-across would make the reader zigzag between two
     documents. Tab still works and still goes across, for anyone who reads the
     other way. */
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    const cells = Array.from(box.current?.querySelectorAll<HTMLInputElement>(
      'input[data-cell]') ?? [])
    const here = cells.indexOf(e.currentTarget)
    const cols = 4
    const next = cells[here + cols]
    if (next) { next.focus(); next.select() } else e.currentTarget.blur()
  }

  const alle = items ?? []
  const list = medFisk ? alle.filter((p) => medFisk.has(p.pen_id)) : alle
  const tomme = alle.length - list.length
  /* Only rows somebody actually touched. An untouched ring is not a ring
     counted as zero — it is a ring nobody has said anything about, and writing
     zeros for it would put eight fabricated observations into a statutory
     record. */
  const filled = list.filter((p) => {
    const r = rows[p.pen_id]
    return r && (r.reason !== '' || r.adult !== '' || r.mobile !== ''
                 || r.sessile !== '')
  })

  const save = async () => {
    setBusy(true); setErr(null); setDone(0)
    let last: { items: PenCount[]; site: PenRollup } | null = null
    try {
      /* One request per ring, in order, and not in parallel. They all resolve
         the same parent site-day row, which is read-then-written rather than
         upserted, so eight concurrent writes would race to create it and seven
         would lose. Sequential is under a second for a farm this size and it is
         the version that is always correct. */
      for (const p of filled) {
        const r = rows[p.pen_id]!
        // A ring that was not counted carries NO numbers, and the receiver
        // refuses a body that says both. The temperature is a fact about the
        // farm's water rather than about the ring, so it goes either way.
        last = await addPenCount(siteId, r.reason !== ''
          ? { pen_id: p.pen_id, counted_at: date,
              not_counted_reason: r.reason, temp_c: num(temp) }
          : { pen_id: p.pen_id, counted_at: date,
              adult_female: num(r.adult), mobile: num(r.mobile),
              sessile: num(r.sessile), n_fish: num(r.n) ?? num(perPen),
              temp_c: num(temp) })
        setDone((n) => n + 1)
      }
      onSaved(last?.items ?? [], last?.site ?? null)
      onClose()
    } catch (e) {
      /* Partial success is reported as partial success. Each ring is its own
         observation and the ones that landed are real; saying "it failed"
         would invite somebody to type all eight again and file four of them
         twice. */
      setErr(e instanceof ApiError ? (e.detail ?? e.code)
        : t('Could not reach the receiver.'))
      if (last) onSaved(last.items, last.site)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card stack merdtelling" style={{ gap: 14 }} ref={box}>
      <div className="row" style={{ justifyContent: 'space-between',
                                    alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('Register a count')}</h2>
        <button className="btn ghost sm" onClick={onClose}>{t('Cancel')}</button>
      </div>

      {err && <div className="notice bad">{err}</div>}

      <div className="tellehode">
        <label className="felt">
          <span>{t('Counted on')}</span>
          <input type="date" value={date} max={today()}
            onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="felt">
          <span>{t('Fish per pen')}</span>
          {/* Entered once. Vedlegg 1 asks for the same number from every ring,
              so the farm-wide field is the truth and the per-row one is the
              exception -- a ring where a gale allowed four fish. */}
          <input inputMode="numeric" value={perPen} style={{ width: 70 }}
            onChange={(e) => setPerPen(e.target.value)} />
        </label>
        <label className="felt">
          <span>{t('Sea temperature')} °C</span>
          <input inputMode="decimal" value={temp} style={{ width: 70 }}
            onChange={(e) => setTemp(e.target.value)} />
        </label>
      </div>

      {items === null ? null : items.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {t('This farm has no pens registered yet.')}
        </p>
      ) : list.length === 0 ? (
        /* Every ring stood empty that day. Not an error and not an empty
           table with headings over nothing — a sentence, because "there was
           nothing to count" is a real answer to give a statutory form. */
        <p className="muted" style={{ margin: 0 }}>
          {t('No pen held fish on this day.')}
        </p>
      ) : (
        <div className="tabellrull">
          <table className="tbl telletabell">
            <thead>
              <tr>
                <th>{t('Pen')}</th>
                <th className="num">{t('Adult female')}</th>
                <th className="num">{t('Mobile')}</th>
                <th className="num">{t('Sessile')}</th>
                <th className="num">{t('Fish')}</th>
                <th>{t('Not counted')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const r = rows[p.pen_id] ?? BLANK
                const skipped = r.reason !== ''
                return (
                  <tr key={p.pen_id} className={skipped ? 'utelt' : undefined}>
                    <td data-l={t('Pen')}>
                      <strong>{p.pen_no}</strong>
                      {p.name && <span className="faint"> {p.name}</span>}
                    </td>
                    {(['adult', 'mobile', 'sessile'] as const)
                      .map((k, i) => (
                        <td key={k} className="num"
                          data-l={[t('Adult female'), t('Mobile'),
                                   t('Sessile')][i]}>
                          <input inputMode="decimal" disabled={skipped}
                            data-cell={`${p.pen_id}-${i}`}
                            value={r[k]} onKeyDown={onKey}
                            onChange={(e) => set(p.pen_id, { [k]: e.target.value })} />
                        </td>
                      ))}
                    <td className="num" data-l={t('Fish')}>
                      <input inputMode="numeric" disabled={skipped}
                        data-cell={`${p.pen_id}-3`}
                        placeholder={perPen}
                        value={r.n} onKeyDown={onKey}
                        onChange={(e) => set(p.pen_id, { n: e.target.value })} />
                    </td>
                    <td data-l={t('Not counted')}>
                      <select value={r.reason}
                        onChange={(e) => set(p.pen_id,
                                             { reason: e.target.value })}>
                        <option value="">—</option>
                        {REASONS.map((x) => (
                          <option key={x} value={x}>
                            {t(`skipped:${x}`).replace('ikke talt — ', '')
                              .replace('ikke talt', t('other'))}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* SAID, NOT SILENTLY DROPPED. A ring missing from a statutory sheet is
          alarming if you do not know why it is missing, and Vedlegg 1 asks for
          every ring — so the sheet has to state that the ones it left out were
          left out because nothing was standing in them, on THIS day. */}
      {tomme > 0 && (
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {tomme === 1
            ? t('One pen held no fish on this day and is not on the sheet.')
            : t('%s pens held no fish on this day and are not on the sheet.')
              .replace('%s', String(tomme))}
        </p>
      )}

      <div className="row" style={{ gap: 10, alignItems: 'center',
                                    flexWrap: 'wrap' }}>
        <button className="btn primary" disabled={busy || filled.length === 0}
          onClick={() => void save()}>
          {busy ? `${t('Saving…')} ${done}/${filled.length}`
            : `${t('Save')} ${filled.length} ${t('pens')}`}
        </button>
        {/* Said before the save and not after. Vedlegg 1 wants every ring, and
            a farm that files six of eight has a gap somebody has to explain --
            better to notice it while the sheet is still on the desk. */}
        {items !== null && filled.length > 0 && filled.length < list.length && (
          <span className="faint" style={{ fontSize: 12 }}>
            {list.length - filled.length}{' '}
            {t('pens are still blank. Vedlegg 1 asks for every one.')}
          </span>
        )}
      </div>
    </div>
  )
}
